import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import galleryManifest from "../data/galleryManifest.json";

const manifestItems = Array.isArray(galleryManifest.items) ? galleryManifest.items : [];
const manifestFilters = Array.isArray(galleryManifest.filters)
  ? galleryManifest.filters.map((filter) => filter.label)
  : [];
const SWIPE_THRESHOLD_PX = 70;

function formatLabel(value) {
  const cleaned = String(value ?? "")
    .replaceAll(/[_-]+/g, " ")
    .replaceAll(/\s+/g, " ")
    .trim()
    .toLowerCase();

  return cleaned.replace(/\b([a-z])/g, (match) => match.toUpperCase());
}

function seededScore(value, seed) {
  const input = `${value}-${seed}`;
  let hash = 2166136261;

  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }

  return (hash >>> 0) / 4294967295;
}

const Gallery = () => {
  const [activeFilter, setActiveFilter] = useState("all");
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [shuffleTick, setShuffleTick] = useState(0);
  const swipeStateRef = useRef({
    isDown: false,
    startX: 0,
    startY: 0,
  });
  const allItems = manifestItems;

  const filterOptions = useMemo(
    () => [
      { value: "all", label: "All Projects" },
      ...manifestFilters.map((filterValue) => ({
        value: filterValue,
        label: formatLabel(filterValue),
      })),
    ],
    []
  );

  const coverImageByCategory = useMemo(() => {
    const map = new Map();
    for (const item of allItems) {
      if (item.type !== "image") continue;
      if (!map.has(item.category)) {
        map.set(item.category, `${process.env.PUBLIC_URL}/${item.path}`);
      }
    }
    return map;
  }, [allItems]);

  const visibleItems = useMemo(() => {
    if (activeFilter === "all") {
      const shuffledItems = [...allItems];
      shuffledItems.sort(
        (a, b) => seededScore(a.id, shuffleTick) - seededScore(b.id, shuffleTick)
      );
      return shuffledItems.slice(0, 9);
    }

    return allItems.filter((item) => item.category === activeFilter);
  }, [activeFilter, allItems, shuffleTick]);

  useEffect(() => {
    if (!selectedItemId) return;
    const existsInGrid = visibleItems.some((item) => item.id === selectedItemId);
    if (!existsInGrid) {
      setSelectedItemId(null);
    }
  }, [visibleItems, selectedItemId]);

  const selectedIndex = useMemo(
    () => visibleItems.findIndex((item) => item.id === selectedItemId),
    [visibleItems, selectedItemId]
  );

  const selectedItem = selectedIndex >= 0 ? visibleItems[selectedIndex] : null;

  const closeLightbox = () => setSelectedItemId(null);

  const goToIndex = (index) => {
    if (!visibleItems.length) return;
    const nextIndex = (index + visibleItems.length) % visibleItems.length;
    setSelectedItemId(visibleItems[nextIndex].id);
  };

  const goPrev = () => goToIndex(selectedIndex - 1);
  const goNext = () => goToIndex(selectedIndex + 1);

  const handleSwipeStart = (event) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    swipeStateRef.current.isDown = true;
    swipeStateRef.current.startX = event.clientX;
    swipeStateRef.current.startY = event.clientY;
  };

  const handleSwipeEnd = (event) => {
    if (!swipeStateRef.current.isDown) return;

    const deltaX = event.clientX - swipeStateRef.current.startX;
    const deltaY = event.clientY - swipeStateRef.current.startY;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);

    if (absX >= SWIPE_THRESHOLD_PX && absX > absY) {
      if (deltaX < 0) {
        goNext();
      } else {
        goPrev();
      }
    }

    swipeStateRef.current.isDown = false;
  };

  const handleSwipeCancel = () => {
    swipeStateRef.current.isDown = false;
  };

  useEffect(() => {
    if (!selectedItem) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKey = (event) => {
      const move = (offset) => {
        if (!visibleItems.length) return;
        const nextIndex = (selectedIndex + offset + visibleItems.length) % visibleItems.length;
        setSelectedItemId(visibleItems[nextIndex].id);
      };

      if (event.key === "Escape") {
        setSelectedItemId(null);
      }
      if (event.key === "ArrowLeft") {
        move(-1);
      }
      if (event.key === "ArrowRight") {
        move(1);
      }
    };

    window.addEventListener("keydown", handleKey);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKey);
    };
  }, [selectedItem, selectedIndex, visibleItems]);

  if (!allItems.length) {
    const sourceHint = galleryManifest.sourceDirectory
      ? `Checked: ${galleryManifest.sourceDirectory}`
      : "No gallery directory with supported files was found.";

    return (
      <section id="gallery" className="relative z-10 mx-auto w-full max-w-6xl px-6 py-12 md:py-16">
        <div className="glass-card p-6">
          <p className="section-kicker">05 Gallery</p>
          <h2 className="section-title mt-3">Project documents and imagery</h2>
          <p className="mt-4 text-sm text-text-secondary">
            No gallery assets were found. Add folders/files to <code>public/assets/images/gallery/&lt;folder&gt;</code>{" "}
            and run <code>npm run build</code> or <code>npm start</code>.
          </p>
          <p className="mt-2 text-xs uppercase tracking-[0.12em] text-[#b9b9b9]">{sourceHint}</p>
        </div>
      </section>
    );
  }

  return (
    <section id="gallery" className="relative z-10 mx-auto w-full max-w-6xl px-6 py-12 md:py-16">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="section-kicker">05 Gallery</p>
          <h2 className="section-title">Project documents and imagery</h2>
        </div>
      </div>

      <div className="mb-6 flex flex-wrap items-center justify-center gap-2 md:gap-3">
        {filterOptions.map((filter) => (
          <button
            key={filter.value}
            type="button"
            className={`gallery-filter ${activeFilter === filter.value ? "gallery-filter-active" : ""}`}
            onClick={() => {
              setActiveFilter(filter.value);
              if (filter.value === "all") {
                setShuffleTick((prev) => prev + 1);
              }
            }}
          >
            {filter.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-4">
        {visibleItems.map((item) => {
          const assetUrl = `${process.env.PUBLIC_URL}/${item.path}`;
          const categoryCover = coverImageByCategory.get(item.category);
          const isPdf = item.type === "pdf";

          return (
            <article key={item.id} className="group relative">
              <button
                type="button"
                onClick={() => setSelectedItemId(item.id)}
                aria-label={`Open ${item.name} preview`}
                title={`Open ${item.name} preview`}
                className="relative block w-full overflow-hidden border border-white/15 bg-black/70 text-left transition duration-200 hover:border-white/40"
              >
                <div className="gallery-uniform-media">
                  {item.type === "image" ? (
                    <img
                      src={assetUrl}
                      alt={item.name}
                      loading="lazy"
                      className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.02]"
                    />
                  ) : (
                    <>
                      {categoryCover ? (
                        <img
                          src={categoryCover}
                          alt={`${formatLabel(item.category)} preview`}
                          loading="lazy"
                          className="h-full w-full object-cover opacity-70 transition duration-300 group-hover:scale-[1.02]"
                        />
                      ) : (
                        <object
                          data={`${assetUrl}#toolbar=0&navpanes=0&scrollbar=0&page=1`}
                          type="application/pdf"
                          aria-label={`${item.name} preview`}
                          className="h-full w-full bg-[#111]"
                        >
                          <div className="gallery-pdf">
                            <span className="gallery-pdf-label">PDF</span>
                          </div>
                        </object>
                      )}
                      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                    </>
                  )}
                </div>
                {isPdf && (
                  <span className="absolute right-2 top-2 border border-white/55 bg-black/75 px-2 py-1 text-[0.58rem] font-semibold uppercase tracking-[0.12em] text-white">
                    PDF
                  </span>
                )}
              </button>
            </article>
          );
        })}
      </div>

      {selectedItem &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 p-4 backdrop-blur-lg"
            role="dialog"
            aria-modal="true"
            aria-label={selectedItem.name}
            onClick={closeLightbox}
          >
            <div
              className="relative max-h-[92vh] w-full max-w-6xl border border-white/25 bg-[#0b0b0b]"
              onClick={(event) => event.stopPropagation()}
            >
            <button
              type="button"
              onClick={closeLightbox}
              aria-label="Close lightbox"
              className="absolute right-3 top-3 z-10 border border-white/45 bg-black/70 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-white hover:bg-black"
            >
              Close
            </button>

            <button
              type="button"
              onClick={goPrev}
              aria-label="Previous item"
              className="absolute left-3 top-1/2 z-10 -translate-y-1/2 border border-white/45 bg-black/70 px-3 py-3 text-xl leading-none text-white hover:bg-black"
            >
              ←
            </button>

            <button
              type="button"
              onClick={goNext}
              aria-label="Next item"
              className="absolute right-3 top-1/2 z-10 -translate-y-1/2 border border-white/45 bg-black/70 px-3 py-3 text-xl leading-none text-white hover:bg-black"
            >
              →
            </button>

            <div
              className="max-h-[92vh] overflow-auto p-3 md:p-4"
              onPointerDown={handleSwipeStart}
              onPointerUp={handleSwipeEnd}
              onPointerCancel={handleSwipeCancel}
              onPointerLeave={handleSwipeCancel}
              style={{ touchAction: "pan-y" }}
            >
              {selectedItem.type === "image" ? (
                <img
                  src={`${process.env.PUBLIC_URL}/${selectedItem.path}`}
                  alt={selectedItem.name}
                  className="mx-auto h-auto max-h-[80vh] w-auto max-w-full object-contain"
                />
              ) : (
                <iframe
                  src={`${process.env.PUBLIC_URL}/${selectedItem.path}#toolbar=1&navpanes=0`}
                  title={selectedItem.name}
                  className="h-[80vh] w-full bg-white"
                />
              )}
            </div>

            <div className="flex items-center justify-between border-t border-white/15 px-4 py-3 text-xs uppercase tracking-[0.12em] text-text-secondary">
              <span>{selectedIndex + 1} of {visibleItems.length}</span>
            </div>
            </div>
          </div>,
          document.body
        )}
    </section>
  );
};

export default Gallery;
